import React, { useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  AdminPortalUser,
  SaveAdminPortalUserRequest,
  useCreateAdminPortalUserMutation,
  useDisableAdminPortalUserMutation,
  useGetAdminPortalUsersQuery,
  useGetCurrentAdminPortalUserQuery,
  useResetAdminPortalUserPasswordMutation,
  useUpdateAdminPortalUserMutation,
} from "../../api/admin/users/AdminUsersApi";

type UserFormValues = SaveAdminPortalUserRequest & {
  sendInvite?: boolean;
};

const roleOptions = [
  { label: "User", value: "user" },
  { label: "Administrator", value: "admin" },
];

function formatDate(value?: string) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}

function getErrorMessage(error: unknown) {
  if (!error || typeof error !== "object") {
    return "Request failed";
  }
  const maybeError = error as { data?: { message?: string }; error?: string };
  return maybeError.data?.message || maybeError.error || "Request failed";
}

export default function AccountManagement() {
  const [profile, setProfile] = useState("user");
  const [editingUser, setEditingUser] = useState<AdminPortalUser | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userForm] = Form.useForm<UserFormValues>();
  const [passwordForm] = Form.useForm();
  const isAdmin = profile === "admin";

  const {
    data: currentUserData,
    error: currentUserError,
    isFetching: isFetchingCurrentUser,
  } = useGetCurrentAdminPortalUserQuery();

  const {
    data: usersData,
    error: usersError,
    isFetching: isFetchingUsers,
  } = useGetAdminPortalUsersQuery(undefined, { skip: !isAdmin });

  const [createUser, { isLoading: isCreatingUser }] = useCreateAdminPortalUserMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateAdminPortalUserMutation();
  const [disableUser, { isLoading: isDisablingUser }] = useDisableAdminPortalUserMutation();
  const [resetPassword, { isLoading: isResettingPassword }] = useResetAdminPortalUserPasswordMutation();

  useEffect(() => {
    Auth.currentSession()
      .then((session) => setProfile(session.getIdToken().payload.profile || "user"))
      .catch(() => setProfile("user"));
  }, []);

  useEffect(() => {
    if (!isUserModalOpen) {
      return;
    }
    userForm.setFieldsValue({
      username: editingUser?.username || "",
      email: editingUser?.email || "",
      name: editingUser?.name || "",
      profile: editingUser?.profile || "user",
      enabled: editingUser?.enabled ?? true,
      sendInvite: true,
      suppressInvite: false,
      temporaryPassword: "",
    });
  }, [editingUser, isUserModalOpen, userForm]);

  const users = usersData?.users || [];
  const currentUser = currentUserData?.user;

  const columns: ColumnsType<AdminPortalUser> = [
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{record.email || record.username}</Typography.Text>
          <Typography.Text type="secondary">{record.username}</Typography.Text>
        </Space>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (value) => value || "-",
    },
    {
      title: "Role",
      dataIndex: "profile",
      filters: roleOptions.map((option) => ({ text: option.label, value: option.value })),
      onFilter: (value, record) => record.profile === value,
      render: (value) => <Tag color={value === "admin" ? "geekblue" : "default"}>{value}</Tag>,
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Space>
          <Tag color={record.enabled ? "green" : "red"}>{record.enabled ? "Enabled" : "Disabled"}</Tag>
          {record.status && <Tag>{record.status}</Tag>}
        </Space>
      ),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      render: formatDate,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space wrap>
          <Button size="small" onClick={() => openEditUser(record)}>
            Edit
          </Button>
          <Button size="small" loading={isResettingPassword} onClick={() => handleResetPassword(record)}>
            Reset Password
          </Button>
          <Popconfirm
            title="Disable this user?"
            okText="Disable"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDisableUser(record)}
            disabled={!record.enabled}
          >
            <Button size="small" danger disabled={!record.enabled} loading={isDisablingUser}>
              Disable
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  function openCreateUser() {
    setEditingUser(null);
    setIsUserModalOpen(true);
  }

  function openEditUser(user: AdminPortalUser) {
    setEditingUser(user);
    setIsUserModalOpen(true);
  }

  async function handleSaveUser(values: UserFormValues) {
    try {
      const body: SaveAdminPortalUserRequest = {
        username: values.username,
        email: values.email,
        name: values.name,
        profile: values.profile,
        enabled: values.enabled,
        temporaryPassword: values.temporaryPassword || undefined,
        suppressInvite: values.sendInvite === false,
      };
      if (editingUser) {
        await updateUser(body).unwrap();
        message.success("User updated");
      } else {
        await createUser(body).unwrap();
        message.success("User created");
      }
      setIsUserModalOpen(false);
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  }

  async function handleDisableUser(user: AdminPortalUser) {
    try {
      await disableUser(user.username).unwrap();
      message.success("User disabled");
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  }

  async function handleResetPassword(user: AdminPortalUser) {
    try {
      await resetPassword(user.username).unwrap();
      message.success("Password reset email sent");
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  }

  async function handleChangePassword(values: { currentPassword: string; newPassword: string }) {
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(cognitoUser, values.currentPassword, values.newPassword);
      passwordForm.resetFields();
      message.success("Password updated");
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Unable to update password");
    }
  }

  const tabs = [
    {
      key: "account",
      label: "My Account",
      children: (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {currentUserError && <Alert type="error" message={getErrorMessage(currentUserError)} />}
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Username">{currentUser?.username || "-"}</Descriptions.Item>
            <Descriptions.Item label="Email">{currentUser?.email || "-"}</Descriptions.Item>
            <Descriptions.Item label="Name">{currentUser?.name || "-"}</Descriptions.Item>
            <Descriptions.Item label="Role">
              <Tag color={profile === "admin" ? "geekblue" : "default"}>{profile}</Tag>
            </Descriptions.Item>
          </Descriptions>
          <Form
            form={passwordForm}
            layout="vertical"
            style={{ maxWidth: 480 }}
            onFinish={handleChangePassword}
          >
            <Form.Item
              label="Current Password"
              name="currentPassword"
              rules={[{ required: true, message: "Current password is required" }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Form.Item
              label="New Password"
              name="newPassword"
              rules={[{ required: true, message: "New password is required" }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={isFetchingCurrentUser}>
              Update Password
            </Button>
          </Form>
        </Space>
      ),
    },
    ...(isAdmin
      ? [{
        key: "users",
        label: "Users",
        children: (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {usersError && <Alert type="error" message={getErrorMessage(usersError)} />}
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button type="primary" onClick={openCreateUser}>
                New User
              </Button>
            </Space>
            <Table
              rowKey="username"
              columns={columns}
              dataSource={users}
              loading={isFetchingUsers}
              scroll={{ x: 900 }}
              pagination={{ pageSize: 20, showSizeChanger: true }}
            />
          </Space>
        ),
      }]
      : []),
  ];

  return (
    <>
      <Typography.Title level={2}>Account Management</Typography.Title>
      <Tabs items={tabs} />
      <Modal
        title={editingUser ? "Edit User" : "New User"}
        open={isUserModalOpen}
        onCancel={() => setIsUserModalOpen(false)}
        onOk={() => userForm.submit()}
        okText={editingUser ? "Save" : "Create"}
        confirmLoading={isCreatingUser || isUpdatingUser}
        destroyOnClose
      >
        <Form form={userForm} layout="vertical" onFinish={handleSaveUser}>
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: "Username is required" }]}
          >
            <Input disabled={Boolean(editingUser)} autoComplete="username" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input autoComplete="email" />
          </Form.Item>
          <Form.Item label="Name" name="name">
            <Input autoComplete="name" />
          </Form.Item>
          <Form.Item
            label="Role"
            name="profile"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select options={roleOptions} />
          </Form.Item>
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          {!editingUser && (
            <>
              <Form.Item label="Temporary Password" name="temporaryPassword">
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Form.Item label="Send Invite" name="sendInvite" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
}
